import { supabase } from '../lib/supabase';
import { downloadDocument } from './storageService';

export interface CacheEntry {
  id: string;
  cache_key: string;
  document_type: string;
  form_data_hash: string;
  tier: 'standard' | 'enhanced' | 'premium';
  cached_content_path: string;
  cached_content_preview: string;
  access_count: number;
  last_accessed_at: string;
  created_at: string;
  provider?: 'claude' | 'openai';
}

const generateHash = async (data: string): Promise<string> => {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

export const generateCacheKey = async (
  formData: Record<string, any>,
  documentType: string,
  tier: 'standard' | 'enhanced' | 'premium'
): Promise<string> => {
  const sortedData = Object.keys(formData)
    .sort()
    .reduce((acc, key) => {
      acc[key] = formData[key];
      return acc;
    }, {} as Record<string, any>);

  const dataString = JSON.stringify(sortedData);
  const hash = await generateHash(`${documentType}:${tier}:${dataString}`);
  return hash;
};

export const generateFormHash = async (
  formData: Record<string, any>,
  documentType: string
): Promise<string> => {
  const sortedData = Object.keys(formData)
    .sort()
    .reduce((acc, key) => {
      acc[key] = formData[key];
      return acc;
    }, {} as Record<string, any>);

  const dataString = JSON.stringify(sortedData);
  const hash = await generateHash(`${documentType}:${dataString}`);
  return hash;
};

export const checkCache = async (
  cacheKey: string
): Promise<CacheEntry | null> => {
  try {
    const { data, error } = await supabase
      .from('document_cache')
      .select('*')
      .eq('cache_key', cacheKey)
      .maybeSingle();

    if (error || !data) return null;

    await updateCacheAccess(data.id);

    return data as CacheEntry;
  } catch (error) {
    console.error('Error checking cache:', error);
    return null;
  }
};

export const getCachedDocument = async (
  cacheEntry: CacheEntry
): Promise<string> => {
  try {
    const content = await downloadDocument(cacheEntry.cached_content_path);
    return content;
  } catch (error) {
    console.error('Error getting cached document:', error);
    throw error;
  }
};

export const saveToCache = async (
  cacheKey: string,
  documentType: string,
  formDataHash: string,
  tier: 'standard' | 'enhanced' | 'premium',
  contentPath: string,
  contentPreview: string,
  provider?: 'claude' | 'openai'
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('document_cache')
      .insert({
        cache_key: cacheKey,
        document_type: documentType,
        form_data_hash: formDataHash,
        tier,
        cached_content_path: contentPath,
        cached_content_preview: contentPreview,
        provider,
      });

    if (error) throw error;
  } catch (error) {
    console.error('Error saving to cache:', error);
  }
};

const updateCacheAccess = async (cacheId: string): Promise<void> => {
  try {
    const { error } = await supabase.rpc('update_cache_access', {
      cache_id: cacheId,
    });

    if (error) throw error;
  } catch (error) {
    console.error('Error updating cache access:', error);
  }
};

export const findSimilarCachedDocuments = async (
  formDataHash: string,
  documentType: string,
  tier: 'standard' | 'enhanced' | 'premium'
): Promise<CacheEntry[]> => {
  try {
    const { data, error } = await supabase
      .from('document_cache')
      .select('*')
      .eq('form_data_hash', formDataHash)
      .eq('document_type', documentType)
      .eq('tier', tier)
      .order('created_at', { ascending: false })
      .limit(5);

    if (error) throw error;

    return (data || []) as CacheEntry[];
  } catch (error) {
    console.error('Error finding similar cached documents:', error);
    return [];
  }
};
